import { useEffect, useState } from 'react';
import getAdhesionDetails, { AdhesionMode } from '../api/users/getAdhesionDetails';
import getCalculatorsUserAportations from '../api/calculators/getCalculatorsUserAportations';
import getUserEppConfigForMode from '../api/users/getUserEppConfigForMode';
import { UserPlanI } from '../api/users/getPlans';
import { UserAdhesionDetailsI } from '../api/users/interfaces/UserAdhesionDetailsI';
import { SingleUserContributionData } from '../api/users/interfaces/ContributionData';
import { UserEppConfigForModeDataI } from '../api/users/interfaces/UserEppConfigForMode';
import { aplanarMovimientos } from '../utils/resumenMulticuenta/movimientos';

/** Lo que el Resumen tiene de UN plan. `null` = no llegó (o falló): «sin dato». */
export interface DatosDePlan {
	cargando: boolean;
	adhesion: UserAdhesionDetailsI[] | null;
	movimientos: SingleUserContributionData[] | null;
	config: UserEppConfigForModeDataI | null;
}

/** Todo el historial, por meses (como Movimientos): lo necesita el coste de los traspasos. */
const MESES_DE_HISTORIAL = 120;

const SIN_DATO = { adhesion: null, movimientos: null, config: null };

/**
 * Lo que el Resumen multicuenta pide por PLAN (chirigota#585, Fase A2, §3.2): la adhesión de
 * Caser (saldo y aportado, para la rentabilidad), los movimientos y la configuración de la
 * aportación. Cada plan en paralelo, y cada llamada por su lado: un plan que falla no tumba los
 * demás, y lo suyo queda en «sin dato».
 *
 * Siempre con `mode` + `userEppConfigId`: con dos planes del mismo tipo, `mode` solo no nombra
 * una fila. Un plan sin tipo no se pide (el backend cruza los dos y devolvería 400).
 *
 * ⚠️ `get-adhesion-details` llama en vivo a Caser: es lo lento, una llamada por plan.
 */
const useDatosDePlanes = (
	token: string | null | undefined,
	plans: UserPlanI[]
): Record<string, DatosDePlan> => {
	const [cargados, setCargados] = useState<Record<string, Omit<DatosDePlan, 'cargando'>>>({});
	// Por contenido, no por identidad: el Dashboard rehace la lista en cada render.
	const clave = plans.map((plan) => `${plan.id}:${plan.type}`).join('|');

	useEffect(() => {
		if (!token) return;
		let vivo = true;
		plans
			.filter((plan) => plan.type)
			.forEach((plan) => {
				const mode = plan.type as AdhesionMode;
				Promise.allSettled([
					getAdhesionDetails(token, mode, plan.id),
					getCalculatorsUserAportations(token, {
						page: 1,
						limit: MESES_DE_HISTORIAL,
						// Estricto y por plan: ver `utils/resumenMulticuenta/movimientos.ts`. Los
						// `productCodes` van para un backend anterior que aún no lea el id.
						userEppConfigId: plan.id,
						productCodes: plan.productCodes
					}),
					getUserEppConfigForMode(token, mode, plan.id)
				]).then(([adhesion, movimientos, config]) => {
					if (!vivo) return;
					setCargados((antes) => ({
						...antes,
						[plan.id]: {
							adhesion:
								adhesion.status === 'fulfilled' && Array.isArray(adhesion.value?.data)
									? adhesion.value.data
									: null,
							// 🔴 Sin lista es «sin dato», no «ninguno»: con «ninguno», un plan a 0 € se daría
							// por vacío y saldría del total del Resumen sin avisar (product#58).
							movimientos:
								movimientos.status === 'fulfilled' && Array.isArray(movimientos.value?.items)
									? aplanarMovimientos(movimientos.value.items)
									: null,
							config:
								config.status === 'fulfilled' && config.value?.success
									? config.value.data?.data ?? null
									: null
						}
					}));
				});
			});
		return () => {
			vivo = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, clave]);

	return Object.fromEntries(
		plans.map((plan) => {
			const cargado = cargados[plan.id];
			if (cargado) return [plan.id, { cargando: false, ...cargado }];
			return [plan.id, { cargando: Boolean(token && plan.type), ...SIN_DATO }];
		})
	);
};

export default useDatosDePlanes;
